import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';

import { useApiMutation } from '@shared/hooks/useApiMutation';

import { addApplicationReview } from '@api/applications/general';

import { DialogButton } from '@ui/uikit/components/common/Dialog/DialogLayout/internals/DialogLayout';
import { Dialog } from '@ui/uikit/components/common/DialogV2';
import { FormControl } from '@ui/uikit/components/common/FormControl';
import { FormTextArea } from '@ui/uikit/components/common/FormTextArea';
import { Modal } from '@ui/uikit/components/common/Modal';
import { Rating } from '@ui/uikit/components/common/Rating';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import styles from './AddFeedbackDialog.module.scss';

const MIN_TEXT_LENGTH = 100;
const MAX_TEXT_LENGTH = 500;

interface ReviewFormValues {
  review_text: string;
  review_rating: number;
}

interface AddReviewDialogProps {
  applicationSlug: string;
  applicationId: number;
  isOpen: boolean;
  onOpenChange: () => void;
}

const AddReviewDialog = ({
                           applicationSlug,
                           applicationId,
                           isOpen,
                           onOpenChange,
                         }: AddReviewDialogProps) => {
  const form = useForm<ReviewFormValues>({
    defaultValues: { review_text: '', review_rating: 0 },
  });
  const { control, handleSubmit, reset } = form;

  const { mutateAsync, isPending } = useApiMutation(addApplicationReview, applicationSlug);

  const handleOpenChange = useCallback(() => {
    reset();
    onOpenChange();
  }, [reset, onOpenChange]);

  const queryClient = useQueryClient();

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await mutateAsync({
        applicationId,
        payload: {
          json: {
            id: applicationId,
            review_text: data.review_text,
            review_rating: data.review_rating.toString(),
            review_title: '',
          },
        },
      });
      handleOpenChange();
      queryClient.invalidateQueries({
        queryKey: ['application', applicationSlug]
      })
    } catch (error) {
      const message = getErrorMessage(error, 'Something went wrong');
      toast.error(`Failed to add review. ${message}`);
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onOpenChange}>
      <Dialog variant="long" size="large">
        <Dialog.Header>Submit Review</Dialog.Header>
        <Dialog.Body className={styles['container']}>
          <form className={styles['form']}>
            <Controller
              control={control}
              name="review_rating"
              rules={{ validate: (value) => (value > 0 ? true : 'This field is required') }}
              render={({ field, fieldState }) => (
                <FormControl
                  label="Rating"
                  isError={!!fieldState.error}
                  hint={fieldState.error?.message}
                  visuallyRequired
                >
                  <Rating.Control {...field} value={field.value} onChange={field.onChange} />
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="review_text"
              rules={{
                required: { value: true, message: 'This field is required' },
                minLength: {
                  value: MIN_TEXT_LENGTH,
                  message: `Minimum length is ${MIN_TEXT_LENGTH}`,
                },
                maxLength: {
                  value: MAX_TEXT_LENGTH,
                  message: `Maximum length is ${MAX_TEXT_LENGTH}`,
                },
              }}
              render={({ field, fieldState }) => (
                <FormTextArea
                  {...field}
                  label="Review"
                  placeholder="Write your review"
                  isError={!!fieldState.error}
                  hint={fieldState.error?.message}
                  maxLength={MAX_TEXT_LENGTH}
                  visuallyRequired
                  rows={3}
                />
              )}
            />
          </form>
        </Dialog.Body>
        <Dialog.ButtonGroup minButtonWidth>
          <DialogButton variant="secondary" onClick={onOpenChange}>
            Close
          </DialogButton>
          <DialogButton onClick={handleFormSubmit} isLoading={isPending}>
            Add Review
          </DialogButton>
        </Dialog.ButtonGroup>
      </Dialog>
    </Modal>
  );
};

export { AddReviewDialog };
